import moment from 'moment';
import hash from 'object-hash';

import { DEFAULT, UPDATE_GLOBAL, UPDATE, CHART_TYPES, UPDATE_HASH, RESET_HASH, DEFAULT_PERCENTILE_TRENDS } from './FilterActions';
import { PERCENTILE_LINE_COLORS } from '../../util/colors';

const common = {
  selectedLists: [],
  selectedLabels: [],
  selectedMembers: []
};

const INITIAL_STATE = {
  chart: 'cfd',
  updated: false,

  allLists: [],
  allQueueLists: [],
  allLabels: [],
  allMembers: [],
  allDones: [],
  allCustomFields:[],

  allPercentileValues: ['30', '50', '70', '85', '95', '98'],
  allPercentiles: [
    { label: '30%', value: '30', color: PERCENTILE_LINE_COLORS[30] },
    { label: '50%', value: '50', color: PERCENTILE_LINE_COLORS[50] },
    { label: '70%', value: '70', color: PERCENTILE_LINE_COLORS[70] },
    { label: '85%', value: '85', color: PERCENTILE_LINE_COLORS[85] },
    { label: '95%', value: '95', color: PERCENTILE_LINE_COLORS[95] },
    { label: '98%', value: '98', color: PERCENTILE_LINE_COLORS[98] }
  ],
  allHealthZones: [
    { label: '30%', value: '30', color: '#c6e9ee' },
    { label: '50%', value: '50', color: '#cdddf3' },
    { label: '70%', value: '70', color: '#f9eabe' },
    { label: '85%', value: '85', color: '#f8d0b5' },
    { label: '95%', value: '95', color: '#f1baba' }
  ],

  dates: {
    startDate: moment()
      .subtract(3, 'months')
      .startOf('day'),
    endDate: moment().endOf('day'),
    singleDate: moment(),
    daysFromStart: 0
  }, 
  analysisDates: {
    startDate: moment()
      .subtract(3, 'months')
      .startOf('day'),
    endDate: moment().endOf('day'),
    singleDate: moment(),
    daysFromStart: 0
  }, 
  dateFormat: 'DD MMM YYYY',
  timePrecision: 'd[d] h[h] m[m]',
  excludeWeekends: 0,
  workStart: '09:00',
  workEnd: '17:00',
  enabledColors: 'labels',
  holidays: [],

  overview: {
    ...common,
    breakdownBy: 'lists',
    selectedQueueLists: [],
    selectedWidgets: [],
    selectedWidgetLayout: [],
    effRange: [0, 100],
    expandedFilters: []
  },
  process: {
    ...common,
    breakdownBy: 'lists',
    selectedQueueLists: [],
    selectedWidgets: [],
    selectedWidgetLayout: [],
    effRange: [0, 100],
    expandedFilters: []
  },  
  cfd: {
    ...common,
    wipStagesTooltip: 'stages',
    approxAvgStagesTooltip: 'stages',
    metrics: true,
    arrivalDepartureLines: true,
    avgChart: true,
    timeUnit: 'month',
    enabledColors: 'labels',
    widgetMode: 'line',
    hash: '',
    expandedFilters: []
  },
  ctsp: {
    ...common,
    selectedPercentiles: [],
    timeUnit: 'day',
    colorsEnabled: true,
    metrics: true,
    trendLine: true,
    blockers: true,
    blockedTime: 'all',
    lineChart: true,
    selectedDate: 'end',
    percentileTrendsBy: DEFAULT_PERCENTILE_TRENDS.trendsBy,
    percentileTrendsPeriod: DEFAULT_PERCENTILE_TRENDS.trendsPeriod,
    percentileTrendsItems: DEFAULT_PERCENTILE_TRENDS.trendsItems,
    hash: '',
    expandedFilters: []
  },
  ctb: {
    ...common,
    timeUnit: 'week',
    breakdown: true,
    breakdownType: 'pie',
    blockers: true,
    blockersChart: true,
    blockedTime: 'all',
    breakdownBy: 'lists',
    hash: '',
    expandedFilters: []
  },
  cth: {
    ...common,
    blockedTime: 'all',
    blockers: true,
    selectedPercentiles: [],
    timeUnit: 'day',
    showNavigator: false,
    avgChart: true,
    hash: '',
    expandedFilters: []
  },
  cta: {
    ...common,
    selectedPercentiles: [],
    selectedHealthZones: ['30', '50', '70', '85', '95'],
    timeUnit: 'day',
    colorsEnabled: true,
    blockers: true,
    blockedTime: 'all',
    blockersChart: true,
    lineChart: true,
    avgChart: true,
    hash: '',
    expandedFilters: [],
    breakdownBy: 'lists',
    showColumns: false,
    wipBreakdownType: 'line',
    wipBreakdownBy: 'lists',
  },
  trc: {
    ...common,
    blockedTime: 'all',
    blockers: true,
    timeUnit: 'day',
    colorsEnabled: true,
    excludeZeroDays: true,
    lineChart: true,
    breakdown: true,
    breakdownType: 'pie',
    enabledColors: 'labels',
    trendLine: true,
    selectedDate: 'end',
    hash: '',
    expandedFilters: []
  },
  th: {
    ...common,
    selectedPercentiles: [],
    timeUnit: 'day',
    colorsEnabled: true,
    excludeZeroDays: true,
    showNavigator: false,
    avgChart: true,
    hash: '',
    expandedFilters: []
  },
  fe: {
    ...common,
    selectedQueueLists: [],
    showNavigator: false,
    avgChart: true,
    effRange: [0, 100],
    chartType: 'column',
    blockedTime: 'all',
    blockers: true,
    hash: '',
    expandedFilters: []
  },
  ddp: {
    ...common,
    timeUnit: 'day',
    lineChart: true,
    dueDateTasks: 'all',
    blockedTime: 'all',
    blockers: true,
    selectedDones: [],
    hash: '',
    expandedFilters: []
  },
  mc: {
    ...common,
    selectedPercentiles: [],
    showNavigator: true,
    date: moment().startOf('day'),
    trials: 10000,
    completedItems: 10,
    scale: 1,
    hash: '',
    expandedFilters: []
  },
  mct: {
    ...common,
    selectedPercentiles: [],
    showNavigator: true,
    startDate: moment().startOf('day'),
    endDate: moment()
      .startOf('day')
      .add(2, 'weeks'),
    trials: 10000,
    scale: 1,
    hash: '',
    expandedFilters: []
  }
};

const FilterReducer = (state = INITIAL_STATE, action) => {
  switch (action.type) {
    case UPDATE: {
      const charts = action.chart || CHART_TYPES;
      let config = { ...state };

      charts.map(chart => {
        const chartCfg = { ...config[chart] };

        config = {
          ...config,
          [chart]: {
            ...chartCfg,
            [action.name]: action.data
          }
        };
      });

      return config;
    }
    case UPDATE_GLOBAL: {
      return {
        ...state,
        updated: !!action.filtersUpdated,
        [action.name]: action.data
      };
    }
    case UPDATE_HASH: {
      const charts = action.chart || CHART_TYPES;
      let config = { ...state };

      charts.map(chart => {
        const hashObj = {
          dashboard: action.dashboard,
          ...config[chart],
          chartName: action.chart ? chart : `global_${chart}`,
          dates: [config.dates.startDate.toISOString(), config.dates.endDate.toISOString()],
          excludeWeekends: config.excludeWeekends,
          workStart: config.workStart,
          workEnd: config.workEnd,
          holidays: config.holidays
        };

        delete hashObj.hash;
        delete hashObj.showNavigator;
        delete hashObj.avgChart;
        delete hashObj.metrics;
        delete hashObj.breakdown;
        delete hashObj.breakdownType;
        delete hashObj.effRange;
        delete hashObj.updateLineChart;
        delete hashObj.expandedFilters;
        delete hashObj.selectedPercentiles;
        delete hashObj.trendLine;
        delete hashObj.selectedDate;
        delete hashObj.wipStagesTooltip;
        delete hashObj.approxAvgStagesTooltip;
        delete hashObj.arrivalDepartureLines;
        delete hashObj.lineChart;
        delete hashObj.selectedQueueLists;
        delete hashObj.wipBreakdownType;
        delete hashObj.wipBreakdownBy;
        if (chart === 'mc' || chart === 'mct') {
          delete hashObj.trials;
          delete hashObj.completedItems;
          delete hashObj.date;
          delete hashObj.startDate;
          delete hashObj.endDate;
          delete hashObj.scale;
        }
        if (chart === 'th' || chart === 'cth') {
          delete hashObj.timeUnit;
        }
        if (chart === 'cfd') {
          delete hashObj.widgetMode;
        }

        const chartCfgHash = hash(hashObj, {
          algorithm: 'md5',
          respectType: false,
          unorderedArrays: true
        });
        const chartCfg = { ...config[chart] };

        config = {
          ...config,
          [chart]: {
            ...chartCfg,
            hash: chartCfgHash
          }
        };
      });

      return config;
    }
    case RESET_HASH: {
      const charts = action.chart || CHART_TYPES;
      let config = { ...state };

      charts.map(chart => {
        const chartCfg = { ...config[chart] };
        config = {
          ...config,
          [chart]: {
            ...chartCfg,
            hash: ''
          }
        };
      });

      return config;
    }
    case DEFAULT: {
      return INITIAL_STATE;
    }
    default: {
      if (!state || typeof state.dates.startDate === 'string') {
        return INITIAL_STATE;
      }
      return state;
    }
  }
};

export default FilterReducer;
